/**
 * Certification Review - Auditor Review Interface
 *
 * Allows auditors to review submitted assessments and make certification decisions.
 * Displays station scores, critical checks, findings, and tier recommendation.
 *
 * Features:
 * - Station score breakdown with pass/fail indicators
 * - Critical checks panel (Benford's Law, Crosswalk)
 * - Findings list with response/verdict workflow
 * - Tier recommendation with override capability
 * - Bridge AI decision preview
 * - Approve/Reject actions
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

interface StationScore {
  score: number;
  passed: boolean;
  details: Record<string, unknown>;
}

interface CriticalCheck {
  passed: boolean;
  p_value?: number;
  coverage?: number;
}

interface AssessmentScores {
  ai_readiness?: number;
  stations?: {
    data_quality?: StationScore;
    ml_readiness?: StationScore;
    ontology?: StationScore;
    fair?: StationScore;
  };
  critical_checks?: {
    benfords_law?: CriticalCheck;
    crosswalk_valid?: CriticalCheck;
  };
}

interface BridgeAIDecision {
  approved_for_gxp: boolean;
  decision_text: string;
  rationale: string;
}

interface AuditFinding {
  finding_id: string;
  station: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  finding_code?: string;
  description: string;
  recommendation?: string;
  vendor_response?: string;
  auditor_verdict?: 'accepted' | 'rejected' | 'pending';
  auditor_notes?: string;
}

interface Assessment {
  assessment_id: string;
  product_id: string;
  product_name: string;
  product_version?: string;
  vendor_id: string;
  vendor_name: string;
  status: string;
  scores?: AssessmentScores;
  decision_tier?: string;
  bridge_ai_decision?: BridgeAIDecision;
  submitted_at?: string;
  evidence_files: Array<{ filename: string; size: number }>;
}

interface TierCalculation {
  tier: 'Platinum' | 'Gold' | 'Silver' | 'Bronze';
  ai_readiness_score: number;
  meets_criteria: boolean;
  reasoning: string;
}

type DecisionTier = 'Platinum' | 'Gold' | 'Silver' | 'Bronze';
type ReviewTabType = 'overview' | 'data_quality' | 'ml_readiness' | 'ontology' | 'fair';

interface EvidenceFile {
  file_id: string;
  filename: string;
  size: number;
  mime_type?: string;
  uploaded_at?: string;
}

// Phase 10.3: Draft state for saving/resuming reviews
interface _ReviewDraftState {
  selectedTier: DecisionTier | null;
  decisionNotes: string;
  tierOverrideJustification: string;
  findingVerdicts: Record<string, { verdict: 'accepted' | 'rejected'; notes: string }>;
  lastSaved?: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const STATION_TAB_CONFIG: Record<ReviewTabType, { label: string; icon: string }> = {
  overview: { label: 'Overview', icon: '📋' },
  data_quality: { label: 'Data Quality', icon: '📊' },
  ml_readiness: { label: 'ML Readiness', icon: '🤖' },
  ontology: { label: 'Ontology', icon: '🔗' },
  fair: { label: 'FAIR', icon: '⚖️' },
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const ScoreGauge: React.FC<{ score: number; label: string; passed?: boolean }> = ({
  score,
  label,
  passed,
}) => {
  const getScoreColor = (s: number) => {
    if (s >= 90) return 'var(--color-success)';
    if (s >= 80) return 'var(--color-info)';
    if (s >= 70) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  return (
    <div className="score-gauge">
      <div className="gauge-circle" style={{ '--score': score } as React.CSSProperties}>
        <svg viewBox="0 0 36 36">
          <path
            className="gauge-bg"
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            className="gauge-fill"
            style={{ stroke: getScoreColor(score) }}
            strokeDasharray={`${score}, 100`}
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          />
        </svg>
        <div className="gauge-value">{Math.round(score)}%</div>
      </div>
      <div className="gauge-label">{label}</div>
      {passed !== undefined && (
        <div className={`gauge-status ${passed ? 'passed' : 'failed'}`}>
          {passed ? 'PASSED' : 'FAILED'}
        </div>
      )}
    </div>
  );
};

const StationCard: React.FC<{
  name: string;
  score?: StationScore;
  icon: string;
}> = ({ name, score, icon }) => {
  if (!score) {
    return (
      <div className="station-card pending">
        <span className="station-icon">{icon}</span>
        <h4>{name}</h4>
        <div className="station-score">--</div>
        <div className="station-status">Not Run</div>
      </div>
    );
  }

  return (
    <div className={`station-card ${score.passed ? 'passed' : 'failed'}`}>
      <span className="station-icon">{icon}</span>
      <h4>{name}</h4>
      <div className="station-score">{Math.round(score.score)}%</div>
      <div className={`station-status ${score.passed ? 'passed' : 'failed'}`}>
        {score.passed ? 'PASSED' : 'FAILED'}
      </div>
    </div>
  );
};

const CriticalCheckCard: React.FC<{
  name: string;
  check?: CriticalCheck;
  icon: string;
  details?: string;
}> = ({ name, check, icon, details }) => {
  if (!check) {
    return (
      <div className="critical-check pending">
        <span className="check-icon">{icon}</span>
        <div className="check-info">
          <h4>{name}</h4>
          <span className="check-status">Not Evaluated</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`critical-check ${check.passed ? 'passed' : 'failed'}`}>
      <span className="check-icon">{check.passed ? '✓' : '✗'}</span>
      <div className="check-info">
        <h4>{name}</h4>
        <span className={`check-status ${check.passed ? 'passed' : 'failed'}`}>
          {check.passed ? 'PASSED' : 'FAILED'}
        </span>
        {details && <span className="check-details">{details}</span>}
      </div>
    </div>
  );
};

const FindingRow: React.FC<{
  finding: AuditFinding;
  onVerdict: (findingId: string, verdict: 'accepted' | 'rejected', notes?: string) => void;
}> = ({ finding, onVerdict }) => {
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return 'var(--color-danger)';
      case 'High': return 'var(--color-warning)';
      case 'Medium': return 'var(--color-info)';
      default: return 'var(--color-muted)';
    }
  };

  return (
    <div className={`finding-row ${finding.auditor_verdict || 'pending'}`}>
      <div className="finding-header" onClick={() => setExpanded(!expanded)}>
        <span
          className="severity-badge"
          style={{ backgroundColor: getSeverityColor(finding.severity) }}
        >
          {finding.severity}
        </span>
        <span className="finding-station">{finding.station}</span>
        <span className="finding-code">{finding.finding_code}</span>
        <span className="finding-expand">{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div className="finding-details">
          <div className="finding-description">
            <h5>Description</h5>
            <p>{finding.description}</p>
          </div>

          {finding.recommendation && (
            <div className="finding-recommendation">
              <h5>Recommendation</h5>
              <p>{finding.recommendation}</p>
            </div>
          )}

          {finding.vendor_response && (
            <div className="finding-vendor-response">
              <h5>Vendor Response</h5>
              <p>{finding.vendor_response}</p>
            </div>
          )}

          {!finding.auditor_verdict && (
            <div className="finding-verdict-form">
              <textarea
                placeholder="Auditor notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
              <div className="verdict-actions">
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => onVerdict(finding.finding_id, 'accepted', notes)}
                >
                  Accept Response
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => onVerdict(finding.finding_id, 'rejected', notes)}
                >
                  Reject Response
                </button>
              </div>
            </div>
          )}

          {finding.auditor_verdict && (
            <div className={`finding-verdict ${finding.auditor_verdict}`}>
              <span className="verdict-label">
                {finding.auditor_verdict === 'accepted' ? '✓ Accepted' : '✗ Rejected'}
              </span>
              {finding.auditor_notes && (
                <p className="verdict-notes">{finding.auditor_notes}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TierBadge: React.FC<{ tier: DecisionTier; size?: 'sm' | 'md' | 'lg' }> = ({
  tier,
  size = 'md',
}) => {
  const tierColors: Record<DecisionTier, string> = {
    Platinum: '#E5E4E2',
    Gold: '#FFD700',
    Silver: '#C0C0C0',
    Bronze: '#CD7F32',
  };

  const tierIcons: Record<DecisionTier, string> = {
    Platinum: '💎',
    Gold: '🥇',
    Silver: '🥈',
    Bronze: '🥉',
  };

  return (
    <div
      className={`tier-badge tier-${tier.toLowerCase()} size-${size}`}
      style={{ '--tier-color': tierColors[tier] } as React.CSSProperties}
    >
      <span className="tier-icon">{tierIcons[tier]}</span>
      <span className="tier-name">{tier}</span>
    </div>
  );
};

// ============================================================================
// PHASE 10.1: STATION TABS COMPONENT
// ============================================================================

const StationTabs: React.FC<{
  activeTab: ReviewTabType;
  onTabChange: (tab: ReviewTabType) => void;
  scores?: AssessmentScores;
}> = ({ activeTab, onTabChange, scores }) => {
  const tabs: ReviewTabType[] = ['overview', 'data_quality', 'ml_readiness', 'ontology', 'fair'];

  const getTabScore = (tab: ReviewTabType): number | null => {
    if (tab === 'overview') return scores?.ai_readiness || null;
    return scores?.stations?.[tab as keyof typeof scores.stations]?.score || null;
  };

  return (
    <div className="station-tabs" style={{
      display: 'flex',
      borderBottom: '2px solid #e5e7eb',
      marginBottom: '24px',
      gap: '4px',
    }}>
      {tabs.map((tab) => {
        const config = STATION_TAB_CONFIG[tab];
        const score = getTabScore(tab);
        const isActive = activeTab === tab;

        return (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              border: 'none',
              borderBottom: isActive ? '3px solid #2563eb' : '3px solid transparent',
              background: isActive ? '#eff6ff' : 'transparent',
              cursor: 'pointer',
              fontWeight: isActive ? 600 : 400,
              color: isActive ? '#2563eb' : '#6b7280',
              transition: 'all 0.2s ease',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <span style={{ fontSize: '18px' }}>{config.icon}</span>
            <span>{config.label}</span>
            {score !== null && (
              <span style={{
                marginLeft: '4px',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: score >= 80 ? '#dcfce7' : score >= 60 ? '#fef3c7' : '#fee2e2',
                color: score >= 80 ? '#166534' : score >= 60 ? '#92400e' : '#991b1b',
              }}>
                {Math.round(score)}%
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// PHASE 10.4: FILE PREVIEW COMPONENT
// ============================================================================

const FilePreviewModal: React.FC<{
  file: EvidenceFile | null;
  assessmentId: string;
  onClose: () => void;
}> = ({ file, assessmentId, onClose }) => {
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return;

    const loadPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const response = await fetch(`/api/assessments/${assessmentId}/files/${file.file_id}/preview`);
        if (!response.ok) throw new Error('Failed to load preview');
        const data = await response.json();
        setPreviewContent(data.preview);
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [file, assessmentId]);

  if (!file) return null;

  const handleDownload = () => {
    window.open(`/api/assessments/${assessmentId}/files/${file.file_id}/download`, '_blank');
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '90%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
      }}>
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px' }}>{file.filename}</h3>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              {(file.size / 1024).toFixed(1)} KB
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={handleDownload}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Download
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 12px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>
        </div>
        <div className="modal-body" style={{
          flex: 1,
          overflow: 'auto',
          padding: '24px',
          backgroundColor: '#f9fafb',
        }}>
          {previewLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner" />
              <p>Loading preview...</p>
            </div>
          )}
          {previewError && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>
              <p>{previewError}</p>
              <p style={{ fontSize: '13px', color: '#6b7280' }}>
                Click Download to view the full file.
              </p>
            </div>
          )}
          {previewContent && (
            <pre style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '13px',
              backgroundColor: '#fff',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              maxHeight: '500px',
              overflow: 'auto',
            }}>
              {previewContent}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 10.3: SAVE DRAFT BUTTON
// ============================================================================

const SaveDraftButton: React.FC<{
  onSave: () => Promise<void>;
  lastSaved?: string;
}> = ({ onSave, lastSaved }) => {
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 16px',
          backgroundColor: '#f3f4f6',
          color: '#374151',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontWeight: 500,
          transition: 'all 0.2s ease',
        }}
      >
        {saving ? (
          <>
            <span className="spinner" style={{ width: '14px', height: '14px' }} />
            Saving...
          </>
        ) : showSaved ? (
          <>
            <span style={{ color: '#16a34a' }}>✓</span>
            Saved
          </>
        ) : (
          'Save Draft'
        )}
      </button>
      {lastSaved && (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Last saved: {new Date(lastSaved).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CertificationReview: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [tierCalculation, setTierCalculation] = useState<TierCalculation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedTier, setSelectedTier] = useState<DecisionTier | null>(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Phase 10.1: Station tabs
  const [activeTab, setActiveTab] = useState<ReviewTabType>('overview');

  // Phase 10.3: Draft state
  const [lastSaved, setLastSaved] = useState<string | undefined>();

  // Phase 10.4: File preview
  const [previewFile, setPreviewFile] = useState<EvidenceFile | null>(null);

  // Phase 10.5: Tier override
  const [tierOverrideJustification, setTierOverrideJustification] = useState('');
  const [isOverridingTier, setIsOverridingTier] = useState(false);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Certification_Review_Guide.md' })
      });
    } catch (err) {
      console.error('[CertificationReview] Error opening guide:', err);
    }
  };

  // Load assessment data
  useEffect(() => {
    const loadAssessment = async () => {
      if (!assessmentId) return;

      try {
        const response = await fetch(`/api/assessments/${assessmentId}`);
        if (!response.ok) {
          throw new Error('Failed to load assessment');
        }

        const data = await response.json();
        setAssessment(data.assessment);
        setFindings(data.findings || []);
        setTierCalculation(data.tier_calculation);

        if (data.tier_calculation?.tier) {
          setSelectedTier(data.tier_calculation.tier);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load assessment');
      } finally {
        setLoading(false);
      }
    };

    loadAssessment();
  }, [assessmentId]);

  // Start review if not already started
  useEffect(() => {
    const startReview = async () => {
      if (assessment?.status === 'submitted') {
        try {
          await fetch(`/api/assessments/${assessmentId}/start-review`, {
            method: 'POST',
          });
          setAssessment((prev) => prev ? { ...prev, status: 'under_review' } : null);
        } catch (err) {
          console.error('Failed to start review:', err);
        }
      }
    };

    if (assessment) {
      startReview();
    }
  }, [assessment?.status, assessmentId, assessment]);

  const handleFindingVerdict = useCallback(async (
    findingId: string,
    verdict: 'accepted' | 'rejected',
    notes?: string
  ) => {
    try {
      const response = await fetch(`/api/findings/${findingId}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict, notes }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit verdict');
      }

      const data = await response.json();
      setFindings((prev) =>
        prev.map((f) => (f.finding_id === findingId ? data.finding : f))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit verdict');
    }
  }, []);

  const handleApprove = useCallback(async () => {
    if (!selectedTier || !assessmentId) return;

    // Check for unresolved critical findings
    const unresolvedCritical = findings.filter(
      (f) => f.severity === 'Critical' && f.auditor_verdict !== 'accepted'
    );

    if (unresolvedCritical.length > 0) {
      setError(`Cannot approve: ${unresolvedCritical.length} unresolved critical finding(s)`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/assessments/${assessmentId}/complete-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision: 'approved',
          tier: selectedTier,
          notes: decisionNotes,
          // Phase 10.5: Include tier override justification if applicable
          tier_override: isOverridingTier ? {
            original_tier: tierCalculation?.tier,
            new_tier: selectedTier,
            justification: tierOverrideJustification,
          } : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to approve');
      }

      // Issue certificate
      await fetch('/api/certifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessment_id: assessmentId }),
      });

      navigate(`/vendors/${assessment?.vendor_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  }, [assessmentId, selectedTier, decisionNotes, findings, navigate, assessment?.vendor_id, isOverridingTier, tierCalculation?.tier, tierOverrideJustification]);

  const handleReject = useCallback(async () => {
    if (!assessmentId) return;

    if (!decisionNotes.trim()) {
      setError('Please provide rejection notes explaining the decision');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`/api/assessments/${assessmentId}/complete-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision: 'rejected',
          notes: decisionNotes,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to reject');
      }

      navigate(`/vendors/${assessment?.vendor_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  }, [assessmentId, decisionNotes, navigate, assessment?.vendor_id]);

  // Phase 10.3: Save draft review
  const handleSaveDraft = useCallback(async () => {
    if (!assessmentId) return;

    try {
      const response = await fetch(`/api/assessments/${assessmentId}/review-draft`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selected_tier: selectedTier,
          decision_notes: decisionNotes,
          tier_override_justification: tierOverrideJustification,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      setLastSaved(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    }
  }, [assessmentId, selectedTier, decisionNotes, tierOverrideJustification]);

  // Phase 10.5: Handle tier override toggle
  const handleTierSelect = (tier: DecisionTier) => {
    setSelectedTier(tier);
    // Check if this is different from recommended tier
    if (tierCalculation && tier !== tierCalculation.tier) {
      setIsOverridingTier(true);
    } else {
      setIsOverridingTier(false);
      setTierOverrideJustification('');
    }
  };

  // Get findings for a specific station (Phase 10.1)
  const _getFindingsForStation = (station: ReviewTabType): AuditFinding[] => {
    if (station === 'overview') return findings;
    return findings.filter((f) => f.station === station);
  };

  if (loading) {
    return (
      <div className="certification-review">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="certification-review">
        <div className="error-container">
          <h2>Assessment Not Found</h2>
          <p>{error || 'The requested assessment could not be found.'}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const scores = assessment.scores;
  const criticalFindings = findings.filter((f) => f.severity === 'Critical');
  const unresolvedCritical = criticalFindings.filter(
    (f) => f.auditor_verdict !== 'accepted'
  );

  return (
    <div className="certification-review">
      {/* Header */}
      <div className="review-header">
        <div className="header-info">
          <button className="btn btn-ghost" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <div className="product-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h1>{assessment.product_name}</h1>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenGuide();
                }}
                className="help-link"
                title="Open Certification Review User Guide"
                style={{
                  fontSize: '1.25rem',
                  textDecoration: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dbeafe';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                  e.currentTarget.style.borderColor = '#bfdbfe';
                }}
              >
                ?
              </a>
            </div>
            <span className="vendor-name">{assessment.vendor_name}</span>
            {assessment.product_version && (
              <span className="version">v{assessment.product_version}</span>
            )}
          </div>
        </div>
        <div className="header-status">
          <span className={`status-badge ${assessment.status}`}>
            {assessment.status.replace('_', ' ').toUpperCase()}
          </span>
          {assessment.submitted_at && (
            <span className="submitted-date">
              Submitted {new Date(assessment.submitted_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Phase 10.3: Save Draft Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <SaveDraftButton onSave={handleSaveDraft} lastSaved={lastSaved} />
      </div>

      {/* Phase 10.1: Station Tabs */}
      <StationTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        scores={scores}
      />

      {/* Main Content */}
      <div className="review-content">
        {/* Left Column - Scores */}
        <div className="scores-column">
          {/* AI Readiness Score */}
          <div className="ai-readiness-section">
            <h2>AI Readiness Score</h2>
            <ScoreGauge
              score={scores?.ai_readiness || 0}
              label="Overall Score"
            />
            {tierCalculation && (
              <div className="tier-recommendation">
                <span className="recommendation-label">Recommended Tier:</span>
                <TierBadge tier={tierCalculation.tier} size="lg" />
                <p className="recommendation-reasoning">{tierCalculation.reasoning}</p>
              </div>
            )}
          </div>

          {/* Station Scores */}
          <div className="stations-section">
            <h2>Station Scores</h2>
            <div className="stations-grid">
              <StationCard
                name="Data Quality"
                score={scores?.stations?.data_quality}
                icon="📊"
              />
              <StationCard
                name="ML Readiness"
                score={scores?.stations?.ml_readiness}
                icon="🤖"
              />
              <StationCard
                name="Ontology"
                score={scores?.stations?.ontology}
                icon="🔗"
              />
              <StationCard
                name="FAIR"
                score={scores?.stations?.fair}
                icon="⚖️"
              />
            </div>
          </div>

          {/* Critical Checks */}
          <div className="critical-checks-section">
            <h2>Critical Checks</h2>
            <div className="critical-checks-list">
              <CriticalCheckCard
                name="Benford's Law Analysis"
                check={scores?.critical_checks?.benfords_law}
                icon="📈"
                details={
                  scores?.critical_checks?.benfords_law?.p_value
                    ? `p-value: ${scores.critical_checks.benfords_law.p_value.toFixed(3)}`
                    : undefined
                }
              />
              <CriticalCheckCard
                name="Crosswalk Validation"
                check={scores?.critical_checks?.crosswalk_valid}
                icon="🔗"
                details={
                  scores?.critical_checks?.crosswalk_valid?.coverage
                    ? `Coverage: ${scores.critical_checks.crosswalk_valid.coverage}%`
                    : undefined
                }
              />
            </div>
          </div>

          {/* Evidence Files (Phase 10.4: with preview) */}
          <div className="evidence-section">
            <h2>Evidence Files ({assessment.evidence_files.length})</h2>
            <ul className="evidence-list">
              {assessment.evidence_files.map((file, index) => (
                <li
                  key={index}
                  className="evidence-file"
                  onClick={() => setPreviewFile({
                    file_id: `file-${index}`,
                    filename: file.filename,
                    size: file.size,
                  })}
                  style={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file.filename}</span>
                  <span className="file-size">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  <span style={{ marginLeft: 'auto', color: '#2563eb', fontSize: '12px' }}>
                    Preview
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right Column - Findings & Decision */}
        <div className="decision-column">
          {/* Findings */}
          <div className="findings-section">
            <h2>
              Audit Findings ({findings.length})
              {unresolvedCritical.length > 0 && (
                <span className="critical-warning">
                  {unresolvedCritical.length} Critical Unresolved
                </span>
              )}
            </h2>
            {findings.length === 0 ? (
              <div className="no-findings">
                <span>✓</span>
                <p>No findings reported</p>
              </div>
            ) : (
              <div className="findings-list">
                {findings.map((finding) => (
                  <FindingRow
                    key={finding.finding_id}
                    finding={finding}
                    onVerdict={handleFindingVerdict}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Bridge AI Preview */}
          {assessment.bridge_ai_decision && (
            <div className="bridge-ai-section">
              <h2>Bridge AI Decision</h2>
              <div
                className={`bridge-decision ${
                  assessment.bridge_ai_decision.approved_for_gxp ? 'approved' : 'not-approved'
                }`}
              >
                <span className="decision-icon">
                  {assessment.bridge_ai_decision.approved_for_gxp ? '✓' : '⚠'}
                </span>
                <div className="decision-content">
                  <h3>{assessment.bridge_ai_decision.decision_text}</h3>
                  <p>{assessment.bridge_ai_decision.rationale}</p>
                </div>
              </div>
            </div>
          )}

          {/* Decision Panel */}
          <div className="decision-panel">
            <h2>Certification Decision</h2>

            <div className="tier-selection">
              <label>Select Certification Tier:</label>
              {tierCalculation && (
                <div style={{
                  marginBottom: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#166534',
                }}>
                  System recommends: <strong>{tierCalculation.tier}</strong>
                </div>
              )}
              <div className="tier-options">
                {(['Platinum', 'Gold', 'Silver', 'Bronze'] as DecisionTier[]).map((tier) => (
                  <button
                    key={tier}
                    className={`tier-option ${selectedTier === tier ? 'selected' : ''}`}
                    onClick={() => handleTierSelect(tier)}
                    disabled={submitting}
                  >
                    <TierBadge tier={tier} size="sm" />
                  </button>
                ))}
              </div>
            </div>

            {/* Phase 10.5: Tier Override Justification */}
            {isOverridingTier && (
              <div className="tier-override-section" style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #f59e0b',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600,
                  color: '#92400e',
                  marginBottom: '8px',
                }}>
                  <span>⚠️</span>
                  Tier Override - Justification Required
                </label>
                <p style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                  You are selecting a tier different from the system recommendation ({tierCalculation?.tier}).
                  Please provide a justification for this override. This will be logged in the audit trail.
                </p>
                <textarea
                  value={tierOverrideJustification}
                  onChange={(e) => setTierOverrideJustification(e.target.value)}
                  placeholder="Explain why you are overriding the recommended tier..."
                  rows={3}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
              </div>
            )}

            <div className="decision-notes">
              <label>Decision Notes:</label>
              <textarea
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                placeholder="Enter notes for your decision..."
                rows={4}
                disabled={submitting}
              />
            </div>

            <div className="decision-actions">
              <button
                className="btn btn-danger btn-lg"
                onClick={handleReject}
                disabled={submitting}
              >
                {submitting ? 'Processing...' : 'Reject Certification'}
              </button>
              <button
                className="btn btn-success btn-lg"
                onClick={handleApprove}
                disabled={
                  submitting ||
                  !selectedTier ||
                  unresolvedCritical.length > 0 ||
                  (isOverridingTier && !tierOverrideJustification.trim())
                }
              >
                {submitting ? 'Processing...' : 'Approve & Issue Certificate'}
              </button>
            </div>

            {unresolvedCritical.length > 0 && (
              <p className="approval-warning">
                Cannot approve: Resolve all critical findings first
              </p>
            )}

            {/* Phase 10.5: Block approve if overriding without justification */}
            {isOverridingTier && !tierOverrideJustification.trim() && (
              <p className="approval-warning" style={{ color: '#92400e' }}>
                Please provide a justification for the tier override
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Phase 10.4: File Preview Modal */}
      {previewFile && assessmentId && (
        <FilePreviewModal
          file={previewFile}
          assessmentId={assessmentId}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export default CertificationReview;
